import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { connect } from 'react-redux';
import ContactTeaser from 'contacts/containers/ContactTeaser/ContactTeaser';
import Tag from 'base/components/Tag';
import Flex from 'base/components/Flex';
import Title4 from 'base/components/Title4';
import MainTitle from 'base/components/MainTitle';
import UnstyledList from 'base/components/UnstyledList';
import FileExplorerTeaser
  from 'dashboard/containers/FileExplorerTeaser/FileExplorerTeaser';
import theme from '../../../theme';
import { search } from '../../store/actions';
import {
  getSearchFacets,
  getSearchKey,
  getSearchQueryParams,
  getSearchResults,
} from '../../store/selectors';

const Wrapper = styled.div`
  margin: 0;
  @media (min-width: ${theme.breakpoints.desktop}) {
    margin: 0 60px 100px 400px;
  }
`;

const SearchColumns = styled(Flex)`
  flex-direction: column;
  @media (min-width: ${theme.breakpoints.desktop}) {
    flex-direction: row;
  }
`;

const EmptyContent = styled.div`
  padding: 45px;
`;

const SearchOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 25px;
  @media (min-width: ${theme.breakpoints.desktop}) {
    padding: 100px 100px 45px 45px;
    width: auto;
    min-width: 30%;
    max-width: 40%;
  }
`;

const Facet = styled(Tag)`
  background: ${theme.colors.darker};
  color: ${theme.colors.light};
  padding: 2px 6px;
  margin: 0 5px 5px 0;
  &:hover {
    color: ${theme.colors.white};
    background: ${theme.colors.primary};
    cursor: pointer;
  }
  ${(props) => props.isActive && css`
    color: ${theme.colors.white};
    background: ${theme.colors.primary};
  `}
`;

const FacetGroup = styled.div`
  padding-bottom: 20px;
  border-bottom: 1px solid ${theme.colors.darker};
  margin-bottom: 20px;
  &:last-child {
    border: 0;
  }
`;


const SearchModal = ({
  results, facets, searchKey, search, queryParams,
}) => {
  const emptyContent = searchKey && searchKey.length <= 1
    ? (
      <EmptyContent>
              Keep typing...
      </EmptyContent>
    )
    : (
      <EmptyContent>
              No results found
      </EmptyContent>
    );

  const resultList = results && results.length > 0 && results.map((result) => {
    switch (result.entityType) {
      case 'folder':
          result.route = (result.space && result.id) // eslint-disable-line
          ? `/spaces/${result.space}/documents/folder/${result.id}` : '/';
        return <FileExplorerTeaser key={`search-result-${result.id}`} entity={result} />;
      case 'document':
        result.route = result.folder // eslint-disable-line
          ? `/spaces/${result.space}/documents/folder/${result.folder}/document/${result.id}`
          : `/spaces/${result.space}/documents/document/${result.id}`;
        return <FileExplorerTeaser key={`search-result-${result.id}`} entity={result} />;
      case 'contact':
        return <ContactTeaser key={`search-result-${result.id}`} entity={result} />;
      default:
        return <></>;
    }
  });

  const sortOptions = (
    <FacetGroup>
      <Title4 style={{ marginBottom: '0.5rem' }}>Sort By</Title4>
      <div>
        <Facet
          isActive={queryParams.sortBy !== 'date'}
          onClick={() => search(searchKey, { sortBy: 'relevance' })}
        >
          Relevance
        </Facet>
        <Facet
          isActive={queryParams.sortBy === 'date'}
          onClick={() => search(searchKey, { sortBy: 'date' })}
        >
          Date
        </Facet>
      </div>
    </FacetGroup>
  );

  const facetList = facets && facets.length > 0 && facets.map((facet) => (
    <FacetGroup key={`facet-${facet.name}`}>
      <Title4 style={{ marginBottom: '0.5rem' }}>
Filter by
        {facet.name}
      </Title4>
      <UnstyledList>
        {facet.values.map((f) => (
          <li key={`facet-${facet.name}-${f.value}`}>
            <Facet
              isActive={f.active}
              onClick={() => search(searchKey, { [facet.key]: f.active ? null : f.name })}
            >
              {f.name}
              {' '}
(
              {f.count}
)
            </Facet>
          </li>
        ))}
      </UnstyledList>
    </FacetGroup>
  ));

  return (
    <Wrapper>
      <SearchColumns>
        <Flex flexDirection="column">
          {results.length > 0 ? resultList : emptyContent}
        </Flex>
        <SearchOptionsWrapper>
          <MainTitle withSpaceBelow>Search options</MainTitle>
          {sortOptions}
          {facetList}
        </SearchOptionsWrapper>
      </SearchColumns>
    </Wrapper>
  );
};

SearchModal.propTypes = {
  results: PropTypes.arrayOf(PropTypes.any).isRequired,
  facets: PropTypes.arrayOf(PropTypes.any).isRequired,
  searchKey: PropTypes.string,
  search: PropTypes.func.isRequired,
  queryParams: PropTypes.objectOf(PropTypes.any).isRequired,
};
SearchModal.defaultProps = {
  searchKey: '',
};

const mapStateToProps = (state) => ({
  results: getSearchResults(state),
  searchKey: getSearchKey(state),
  facets: getSearchFacets(state),
  queryParams: getSearchQueryParams(state),
});
const mapDispatchToProps = {
  search,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SearchModal);
